"use client";
import { useCallback, useEffect, useState } from "react";

export type Language = "ms" | "en";

const STORAGE_KEY = "tulus:lang";
const CHANGE_EVENT = "tulus:lang-changed";

function readStored(): Language {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "ms" ? "ms" : "en";
  } catch {
    return "en";
  }
}

// One language for the whole site, chosen from the toggle in the header.
// Same pattern as tuxgeo.com (root AGENTS.md 12.7). English is the default:
// Azrin's call on 2026-10-12, since the site's first audience is institutions;
// the brand guide's Malay-first voice still governs the Malay copy.
//
// Every consumer calls this hook, so the state is shared without a provider:
// localStorage holds the choice and a window event tells every mounted
// instance to re-read it. Always "en" on the first render, because the export
// is prerendered in English and reading storage during render would disagree
// with that HTML; the stored choice is applied in an effect.
export function useLanguage(): [Language, (next: Language) => void] {
  const [lang, setLangState] = useState<Language>("en");

  useEffect(() => {
    setLangState(readStored());
    const onChange = () => setLangState(readStored());
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("storage", onChange); // another tab switched
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Language) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Blocked storage: the choice applies to this page but not after a reload.
    }
    setLangState(next);
    try {
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    } catch {
      // Without CustomEvent the other components catch up on their next mount.
    }
  }, []);

  return [lang, setLang];
}

/**
 * Keep the tab title in the chosen language. Metadata is prerendered in English,
 * and Next.js writes its <title> again after hydration and client navigation,
 * which would undo a one-off assignment. It can stream that <title> into the
 * body rather than the head, so watch the whole document and reapply.
 */
export function useDocumentTitle(title: string) {
  useEffect(() => {
    const apply = () => {
      if (document.title !== title) document.title = title;
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.documentElement, { subtree: true, childList: true, characterData: true });
    return () => observer.disconnect();
  }, [title]);
}
